import { getClientKey, initiatePayment, logClientEvent } from "../services/PaytraceServices";
import { loadProtectJs } from "../services/protectLoader";
import React, { useEffect, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Box } from "@mui/material";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
// import { useSnackbar } from "../context/SnackbarContext";

export default function ProtectPayment({
  userData,
  paymentPopupClose,
  onPaymentResponse,
  onPaymentError,
}) {
  const [clientKey, setClientKey] = useState("");
  const [instance, setInstance] = useState(null);
  const { executeRecaptcha } = useGoogleReCaptcha();
  // const showSnackbar = useSnackbar();

  // Customer information collected in React form (above PayTrace iframe)
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    zipcode: "",
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  // Init failure state: when set, the spinner is replaced by an error message
  // with a Try Again button instead of spinning forever.
  const [initError, setInitError] = useState(null);
  const [initAttempt, setInitAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        await loadProtectJs();
        const key = await getClientKey();
        if (cancelled) return;
        if (!key) {
          setInitError("The secure payment form could not be loaded.");
          logClientEvent("getClientKey", "No client key returned", userData?.rptID);
          return;
        }
        setClientKey(key);
      } catch (error) {
        if (cancelled) return;
        setInitError("The secure payment form could not be loaded.");
        logClientEvent("protectJsLoad", error?.message || error, userData?.rptID);
      }
    }
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAttempt]);

  useEffect(() => {
    if (clientKey) {
      // PTPayment.setup reports nothing when it fails — without this timeout
      // the popup would show the loading spinner forever.
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          setInitError("The secure payment form did not finish loading.");
          logClientEvent("ptSetupTimeout", "PTPayment.setup gave no callback within 12s", userData?.rptID);
        }
      }, 12000);

      try {
        window.PTPayment.setup(
          {
            styles: {
            code: {
              font_color: "black",
              border_color: "#ccc",
              border_style: "solid",
              font_size: "15px",
              input_border_radius: "4px",
              input_border_width: "1px",
              input_border_color: "#ccc",
              input_font: "arial, sans-serif, serif",
              input_font_weight: "500",
              input_margin: "5px 0px 5px 48.90px",
              input_padding: "0px 0px 0px 0px",
              label_color: "black",
              label_size: "16px",
              label_width: "160px",
              label_font: "arial, sans-serif, serif",
              label_font_weight: "normal",
              label_margin: "5px 0px 0px 48.90px",
              label_padding: "2px 0px 2px 0px",
              background_color: "white",
              label_border_style: "none",
              height: "30px",
              width: "100%",
              padding_bottom: "2px",
            },
            cc: {
              border_color: "#ccc",
              border_style: "solid",
              input_border_radius: "4px",
              input_border_width: "1px",
              input_border_color: "#ccc",
              height: "30px",
              width: "100%",
              type: "dropdown",
              font_size: "13pt",
              label_size: "16px",
              font_color: "black",
              label_color: "black",
              label_width: "120px",
              input_font_weight: "400",
              background_color: "white",
              label_margin: "8px 0px 5px 0px",
              label_padding: "0px 0px 1px 0px",
              input_font: "arial, sans-serif, serif",
              label_font: "arial, sans-serif, serif",
            },
            exp: {
              border_color: "#ccc",
              border_style: "solid",
              font_color: "black",
              font_size: "15px",
              input_border_radius: "4px",
              input_border_width: "1px",
              input_border_color: "#ccc",
              input_font: "arial, sans-serif, serif",
              input_font_weight: "400",
              input_margin: "5px 0px 5px 0px",
              input_padding: "0px 0px 0px 0px",
              label_color: "black",
              label_size: "16px",
              label_width: "86px",
              label_font: "arial, sans-serif, serif",
              label_font_weight: "normal",
              label_margin: "5px 0px 0px 0px",
              label_padding: "2px 0px 2px 0px",
              background_color: "white",
              height: "30px",
              // width: "57px",
              padding_bottom: "2px",
              type: "dropdown",
            },
            body: {
              background_color: "#f8f9fa",
            },
          },
          authorization: { clientKey: clientKey },
        },
        (instance) => {
          settled = true;
          clearTimeout(timer);
          window.protectInstance = instance;
          setInstance(instance);
        }
      );
      } catch (error) {
        settled = true;
        clearTimeout(timer);
        console.error("PayTrace setup failed:", error);
        setInitError("The secure payment form could not be loaded.");
        logClientEvent("ptSetupError", error?.message || error, userData?.rptID);
      }

      return () => {
        settled = true;
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientKey]);

  // Full restart of the init sequence (script load -> client key -> setup)
  const retryInit = () => {
    setInitError(null);
    setClientKey("");
    setInstance(null);
    setInitAttempt((a) => a + 1);
  };

  const processPayment = async (e) => {
    setIsButtonDisabled(true);
    e.preventDefault();
    e.stopPropagation();

    // Get reCAPTCHA v3 token on-demand
    if (!executeRecaptcha) {
      alert("reCAPTCHA not ready. Please try again.");
      setIsButtonDisabled(false);
      return;
    }

    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha("payment");
    } catch (err) {
      console.error("reCAPTCHA execution failed:", err);
      alert("reCAPTCHA verification failed. Please try again.");
      setIsButtonDisabled(false);
      return;
    }

    if (instance === null) {
      console.error("Protect.js instance not initialized.");
      setIsButtonDisabled(false);
      return;
    }

    setIsPaymentProcessing(true); // Show loader when payment starts processing

    instance
      .process()
      .then((response) => {
      //  showSnackbar("Payment tokenized successfully", "success");
        try {
          const { hpf_token, enc_key } = response.message;

          if (!hpf_token || !enc_key || userData.amount <= 0) {
            alert("Invalid payment details");
            paymentPopupClose({ isPaymentPopupOpen: false });
            return;
          }

          const payment = async () => {
            try {
              // Pass customer information collected from React form
              const paymentDetails = await initiatePayment(
                userData.amount,
                hpf_token,
                enc_key,
                {
                  ...userData, // Billing address info (address, city, state)
                  ...customerInfo // Customer info from React form (name, email, phone, zipcode) - this overrides userData zipcode
                },
                userData.paymentNote,
                userData.rptID,
                userData.selectedInspections, // Pass selected inspection types
                recaptchaToken // Pass reCAPTCHA token for bot protection
              );
              console.log("paymentDetails", paymentDetails?.status);
              console.log("payment response_code", paymentDetails?.data?.data?.response_code);
              
              // Check if HTTP request was successful and response_code is 101 (Transaction approved)
              if (paymentDetails?.status === 200 && 
                  paymentDetails?.data?.status === "success" && 
                  paymentDetails?.data?.data?.response_code === 101) {
                onPaymentResponse({ 
                  result: paymentDetails?.data?.data,
                  customerInfo: { ...customerInfo } // Pass customer info to the success callback
                });
                // Reset customer info on successful payment
                setCustomerInfo({
                  name: "",
                  email: "",
                  phone: "",
                  zipcode: "",
                });
              } else {
                // Payment failed - show receipt with error details
                const responseCode = paymentDetails?.data?.data?.response_code;
                const responseMessage = paymentDetails?.data?.data?.response_message || "Payment failed";
                console.log(`Payment failed with code ${responseCode}: ${responseMessage}`);

                // A pre-gateway rejection never reaches PayTrace, so there is no
                // response_code to render - only the backend's explanation. Without
                // this the receipt showed "Transaction Failed" and nothing else.
                const backendError = paymentDetails?.data?.error;
                const result = paymentDetails?.data?.data || (backendError
                  ? {
                      error_message: backendError,
                      error_code: paymentDetails?.data?.code,
                      response_message: backendError,
                    }
                  : undefined);

                // Show receipt for failed payment as well
                onPaymentResponse({
                  result,
                  customerInfo: { ...customerInfo } // Pass customer info to the receipt
                });
                
                // Reset customer info after failed payment too
                setCustomerInfo({
                  name: "",
                  email: "",
                  phone: "",
                });
              }
            } catch (error) {
              console.error("Error processing payment:", error);
            } finally {
              setIsPaymentProcessing(false);
              setIsButtonDisabled(false);
            }
          };

          payment();
        } catch (error) {
          console.error("Error processing payment:", error);
          setIsPaymentProcessing(false); // Hide loader if error occurs
        }
      })
      .catch((error) => {
        console.error("Tokenization failed:", error);

        // Log full error details for debugging
        console.log("Full error object:", JSON.stringify(error, null, 2));

        // Extract the error message from PayTrace Protect.js
        let errorMessage = "Payment tokenization failed. Please try again.";
        if (error && error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && error.reason) {
          errorMessage = error.reason;
        }

        console.log("PayTrace tokenization error message:", errorMessage);
        onPaymentError(errorMessage);
        setIsPaymentProcessing(false); // Hide loader if tokenization fails
      });
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Limit to 10 digits
    const limitedPhoneNumber = phoneNumber.slice(0, 10);
    
    // Format as (XXX) XXX-XXXX
    if (limitedPhoneNumber.length < 4) {
      return limitedPhoneNumber;
    } else if (limitedPhoneNumber.length < 7) {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3)}`;
    } else {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3, 6)}-${limitedPhoneNumber.slice(6)}`;
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      // Format phone number as user types
      const formattedPhone = formatPhoneNumber(value);
      setCustomerInfo(prev => ({
        ...prev,
        [field]: formattedPhone
      }));
    } else {
      setCustomerInfo(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation function (only if phone is entered)
  const isValidPhone = (phone) => {
    if (!phone.trim()) return true; // Phone is optional
    const cleanPhone = phone.replace(/[^\d]/g, ''); // Extract only digits
    return cleanPhone.length === 10; // Must be exactly 10 digits for US format
  };

  // Zipcode validation function - only 5 digits
  const isValidZipcode = (zipcode) => {
    if (!zipcode.trim()) return false; // Zipcode is required
    const cleanZip = zipcode.replace(/[^\d]/g, ''); // Extract only digits
    return /^\d{5}$/.test(cleanZip); // Must be exactly 5 digits
  };

  const isFormValid = () => {
    return customerInfo.name.trim() !== "" &&
           customerInfo.email.trim() !== "" &&
           customerInfo.zipcode.trim() !== "" &&
           isValidEmail(customerInfo.email) &&
           isValidPhone(customerInfo.phone) &&
           isValidZipcode(customerInfo.zipcode);
  };

  return (
    <Box>
      {!initError && (!clientKey || !instance) && (
        <DotLottieReact
          src="https://lottie.host/e770bf96-9e0b-4590-8e99-e7ed44c69f10/Aevhy2n5Lv.lottie"
          loop
          autoplay
        />
      )}

      {/* Load/setup failed — offer a retry instead of an endless spinner */}
      {initError && (
        <Box textAlign="center" py={5} px={2}>
          <p style={{ color: "#d32f2f", fontWeight: 500, marginBottom: "6px" }}>
            {initError}
          </p>
          <p style={{ color: "#555", fontSize: "14px", marginBottom: "20px" }}>
            This is usually a temporary connection issue. Please try again.
          </p>
          <button
            onClick={retryInit}
            style={{
              background: "linear-gradient(#ee3a43, #d90000)",
              color: "#fff",
              border: "none",
              padding: "12px 24px",
              borderRadius: "4px",
              fontSize: "1.1em",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </Box>
      )}

      {/* Render form only when instance is available */}
      {instance && !isPaymentProcessing && (
        <Box mb={3}>
          <h4 style={{ margin: "0 0 15px 0", color: "#305496" }}>Customer Information</h4>
          
          {/* Name Row (Full Width) */}
          <Box mb={2} px={1}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
              Name on Card <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              value={customerInfo.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                border: customerInfo.name.trim() === "" && customerInfo.name !== "" ? "2px solid #ff6b6b" : "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "15px",
                boxSizing: "border-box",
                outline: "none",
                transition: "border-color 0.2s ease"
              }}
              onFocus={(e) => e.target.style.borderColor = "#2196f3"}
              onBlur={(e) => e.target.style.borderColor = customerInfo.name.trim() === "" && customerInfo.name !== "" ? "#ff6b6b" : "#ccc"}
              placeholder="Enter cardholder name"
            />
          </Box>

          {/* Email and Phone Row */}
          <Box display="flex" gap={2} mb={2} px={1}>
            <Box flex={1}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                Email Address <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="email"
                value={customerInfo.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: customerInfo.email.trim() !== "" && !isValidEmail(customerInfo.email) ? "2px solid #ff6b6b" : "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "15px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "border-color 0.2s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#2196f3"}
                onBlur={(e) => e.target.style.borderColor = customerInfo.email.trim() !== "" && !isValidEmail(customerInfo.email) ? "#ff6b6b" : "#ccc"}
                placeholder="Enter email address"
              />
              {customerInfo.email.trim() !== "" && !isValidEmail(customerInfo.email) && (
                <span style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "2px", display: "block" }}>
                  Please enter a valid email address
                </span>
              )}
            </Box>
            <Box flex={1}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                Phone Number
              </label>
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: customerInfo.phone.trim() !== "" && !isValidPhone(customerInfo.phone) ? "2px solid #ff6b6b" : "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "15px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "border-color 0.2s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#2196f3"}
                onBlur={(e) => e.target.style.borderColor = customerInfo.phone.trim() !== "" && !isValidPhone(customerInfo.phone) ? "#ff6b6b" : "#ccc"}
                placeholder="(123) 456-7890"
              />
              {customerInfo.phone.trim() !== "" && !isValidPhone(customerInfo.phone) && (
                <span style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "2px", display: "block" }}>
                  Please enter a valid 10-digit phone number
                </span>
              )}
            </Box>
          </Box>

          {/* Zipcode Row */}
          <Box mb={2} px={1}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
              Billing ZIP Code <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              value={customerInfo.zipcode}
              onChange={(e) => handleInputChange("zipcode", e.target.value)}
              style={{
                width: "200px",
                padding: "10px",
                border: customerInfo.zipcode.trim() !== "" && !isValidZipcode(customerInfo.zipcode) ? "2px solid #ff6b6b" : "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "15px",
                boxSizing: "border-box",
                outline: "none",
                transition: "border-color 0.2s ease"
              }}
              onFocus={(e) => e.target.style.borderColor = "#2196f3"}
              onBlur={(e) => e.target.style.borderColor = customerInfo.zipcode.trim() !== "" && !isValidZipcode(customerInfo.zipcode) ? "#ff6b6b" : "#ccc"}
              placeholder="12345"
              maxLength="5"
            />
            {customerInfo.zipcode.trim() !== "" && !isValidZipcode(customerInfo.zipcode) && (
              <span style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "2px", display: "block" }}>
                Please enter a valid 5-digit ZIP code
              </span>
            )}
          </Box>
        </Box>
      )}

      {/* Payment Card Information - Always visible when clientKey is available */}
      {clientKey && !initError && (
        <Box mb={3}>
          <h4 style={{ margin: "0 0 15px 0", color: "#305496" }}>Payment Information</h4>
          <Box id="pt_hpf_form"></Box>
        </Box>
      )}

      {/* Processing animation below Payment Information */}
      {isPaymentProcessing && (
        <Box display="flex" justifyContent="center" mb={3}>
          <DotLottieReact
            src="https://lottie.host/e770bf96-9e0b-4590-8e99-e7ed44c69f10/Aevhy2n5Lv.lottie"
            loop
            autoplay
          />
        </Box>
      )}

      {instance && !isPaymentProcessing && (
        <Box display="flex" justifyContent="center" alignItems="center" marginBottom="15px"
          sx={{ opacity: 0.7, gap: "8px" }}
        >
          <img
            src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
            alt="reCAPTCHA"
            style={{ width: 24, height: 24 }}
          />
          <span style={{ fontSize: "12px", color: "#555" }}>
            Protected by reCAPTCHA —{" "}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#555" }}>Privacy</a>
            {" · "}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#555" }}>Terms</a>
          </span>
        </Box>
      )}

      {instance && !isPaymentProcessing && (
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          marginTop="0px"
        >
          <button
            disabled={isButtonDisabled || !isFormValid()}
            style={
              isButtonDisabled || !isFormValid()
                ? {
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "4px",
                    fontSize: "1.1em",
                    transition: "opacity 0.2s ease",
                    opacity: "0.5",
                    cursor: "not-allowed",
                    background: "linear-gradient(#ee3a43, #d90000)",
                    color: "white",
                  }
                : {
                    background: "linear-gradient(#ee3a43, #d90000)",
                    color: "#fff",
                     border: "none",
                     padding: "12px 24px",
                     borderRadius: "4px",
                     fontSize: "1.1em",
                     cursor: "pointer",
                     transition: "opacity 0.2s ease",
                  }
            }
            onClick={processPayment}
          >
            Submit Payment
          </button>
        </Box>
      )}
    </Box>
  );
}
